import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ImagePlus, Loader2, X } from "lucide-react";
import { codeIssue, imageUrl, seasonFromCode } from "@/lib/modelSheets";
import { SizesInput } from "@/components/modelsheets/SizesInput";

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>
);

const ImageBox = ({ s, ensureSaved, onImage }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const img = imageUrl(s);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Immagine troppo grande (massimo 10 MB)"); return; }
    setBusy(true);
    try {
      const saved = await ensureSaved();
      const fd = new FormData();
      fd.append("file", file);
      onImage((await api.post(`/model-sheets/${saved.id}/image`, fd)).data);
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Caricamento non riuscito");
    } finally { setBusy(false); }
  };
  const remove = async () => onImage((await api.delete(`/model-sheets/${s.id}/image`)).data);
  return (
    <div className="relative flex h-64 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-secondary/30" data-testid="sheet-image-box">
      {img ? <img src={img} alt="Disegno del capo" className="h-full w-full object-contain" data-testid="sheet-image" /> : (
        <button type="button" onClick={() => ref.current?.click()} className="flex flex-col items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="sheet-image-upload">
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-7 w-7" />}Carica foto o disegno del capo
        </button>
      )}
      {img && (
        <div className="absolute right-2 top-2 flex gap-1">
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => ref.current?.click()} disabled={busy} data-testid="sheet-image-replace">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Cambia"}</Button>
          <Button size="icon" variant="secondary" className="h-7 w-7" onClick={remove} data-testid="sheet-image-remove"><X className="h-3.5 w-3.5" /></Button>
        </div>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="sheet-image-input" />
    </div>
  );
};

export const ModelFields = ({ s, set, ensureSaved, onImage }) => {
  const f = (k) => (e) => set({ ...s, [k]: e.target.value });
  const codeSeason = seasonFromCode(s.code);
  const setCode = (e) => { const code = e.target.value; set({ ...s, code, season: seasonFromCode(code) || s.season }); };
  return (
    <div className="grid gap-6 md:grid-cols-[1fr_320px]">
      <div className="grid grid-cols-2 content-start gap-3">
        <Field label="Nome del modello"><Input value={s.name} onChange={f("name")} placeholder="es. Abito Sofia" data-testid="sheet-field-name" /></Field>
        <Field label="Taglia base">
          <Select value={s.base_size || ""} onValueChange={(v) => set({ ...s, base_size: v })}>
            <SelectTrigger data-testid="sheet-field-base-size"><SelectValue placeholder="Scegli la taglia base" /></SelectTrigger>
            <SelectContent>{(s.sizes || []).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Codice modello (come su Shopify)">
          <Input value={s.code} onChange={setCode} placeholder="es. 8001 A26" data-testid="sheet-field-code" className={codeIssue(s.code, s.season) ? "border-amber-500/60" : ""} />
          {codeIssue(s.code, s.season) && <p className="flex gap-1 text-[11px] text-amber-400" data-testid="sheet-code-warning"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{codeIssue(s.code, s.season)}</p>}
        </Field>
        <Field label="Stagione (dal codice modello)">
          <Input value={codeSeason || ""} readOnly disabled placeholder="Si compila dal codice, es. 8001 A26" data-testid="sheet-field-season" />
        </Field>
        <Field label="Taglie del modello" className="col-span-2"><SizesInput value={s.sizes} onChange={(sizes) => set({ ...s, sizes })} /></Field>
      </div>
      <Field label="Foto o disegno del capo"><ImageBox s={s} ensureSaved={ensureSaved} onImage={onImage} /></Field>
    </div>
  );
};
